import { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AudioPlayer, Icon, DateDisplay } from '@aidkitorg/component-library';
import { safeParse, renderLinks, wrapWithBdi } from '../../../../Util';
import { Message } from '../../types';
import { AudioPlayerWithSignedUrl } from './AudioPlayerWithSignedUrl';
import { useLocalizedStrings } from '../../../../Localization';
import { usePost } from '../../../../API';
import { getURLsFromCSV } from '@aidkitorg/roboscreener/lib/util/urls_from_csv';

interface MessageBubbleProps {
  message: Message;
  isLast: boolean;
  applicantInfo?: any;
}

// Hook to get signed URL for first attachment (used for selfie)
function useFirstSignedURL(attachments?: string) {
  const getSignedURL = usePost('/document/view');
  const urls = attachments ? getURLsFromCSV(attachments) : [];
  const firstUrl = urls[0];

  const { data: signedURL } = useQuery({
    queryKey: ['firstSignedUrl', firstUrl],
    queryFn: async () => {
      const paths = await getSignedURL({ paths: [firstUrl] });
      return paths?.paths?.[firstUrl] || '';
    },
    enabled: !!attachments && !!firstUrl,
    staleTime: 5 * 60 * 1000, // Consider signed URLs fresh for 5 minutes
  });

  return signedURL || '';
}

export const MessageBubble: FC<MessageBubbleProps> = ({ message, applicantInfo }) => {
  const L = useLocalizedStrings();
  const metadata = safeParse(message.metadata || '{}');

  // Get applicant selfie for avatar
  const Selfie = useFirstSignedURL(applicantInfo?.['selfie']);

  // Parse scheduled send time from metadata
  const scheduledSendTime = metadata.sendAt ? new Date(metadata.sendAt) : null;
  const displayTime = scheduledSendTime || new Date(message.created_at);
  const timestampPrefix = scheduledSendTime ? L.applicant.comms.for + ' ' : '';

  // Helper to format message text with phone/link detection
  const prettyTextMessage = (text: string, blankWhatsAppMsg?: boolean) => {
    if (blankWhatsAppMsg) {
      return <em>{L.applicant.comms.blank_whatsapp_message}</em>;
    }
    if (!text) return <></>;

    // Trim to remove leading/trailing whitespace
    const trimmedText = text.trim();
    if (trimmedText === '--') {
      return <small><em>{L.applicant.comms.media_message}</em></small>;
    }

    const phoneNumberRegex = /((?:\+?\d{1,3}[\s.-]?)?(?:(?:\(\d{3}\))|\d{3})[\s.-]?\d{3}[\s.-]?\d{4})/g;

    // Split by newlines, then process each line for phone numbers and links
    const lines = trimmedText.split('\n');
    return (
      <>
        {lines.map((line, lineIdx) => {
          const wrappedParts = line.split(phoneNumberRegex)
            .filter(x => !!x)
            .map((part, idx) =>
              phoneNumberRegex.test(part)
                ? <span key={`phone-${lineIdx}-${idx}`}>{wrapWithBdi(part)}</span>
                : <span key={`link-${lineIdx}-${idx}`}>{renderLinks(part)}</span>
            );
          return (
            <span key={`line-${lineIdx}`}>
              {wrappedParts}
              {lineIdx < lines.length - 1 && <br />}
            </span>
          );
        })}
      </>
    );
  };

  // Determine direction: inbound if needs_attention or has certain metadata
  const direction = message.needs_attention || metadata.handled_by || metadata.ToCountry || metadata.ApiVersion || metadata.eventSource === "aws:ses" || metadata.isInboundVoiceRecordingDisabled
    ? 'inbound'
    : 'outbound';

  const author = direction === 'inbound'
    ? (applicantInfo?.['legal_name'] || message.author_name || message.source)
    : (message.author_name || 'System');

  // Status badge for messages
  const getStatusBadge = () => {
    if (direction === 'inbound' && message.needs_attention) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
          {L.applicant.comms.new_unhandled}
        </span>
      );
    }
    if (message.status) {
      const statusColors: Record<string, string> = {
        'delivered': 'bg-green-100 text-green-800',
        'sent': 'bg-blue-100 text-blue-800',
        'failed': 'bg-red-100 text-red-800',
        'queued': 'bg-gray-100 text-gray-800',
      };
      const statusMap: Record<string, string> = {
        'delivered': L.applicant.comms.delivered,
        'sent': L.applicant.comms.sent,
        'failed': L.applicant.comms.failed,
        'queued': L.applicant.comms.queued,
      };
      const colorClass = statusColors[message.status] || 'bg-gray-100 text-gray-800';
      const statusLabel = statusMap[message.status] || message.status.charAt(0).toUpperCase() + message.status.slice(1);
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
          {statusLabel}
        </span>
      );
    }
    return null;
  };

  return (
    <div className="relative">
      <div className="relative flex gap-3">
        {/* Avatar */}
        <div
          className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center overflow-hidden ${
            author === 'System' ? 'bg-gray-300' : direction === 'inbound' ? 'bg-gray-300' : 'bg-blue-600'
          }`}
        >
          {author === 'System' ? (
            <img
              src="/aidkit-social-black.png"
              alt="AidKit"
              className="w-full h-full object-cover"
            />
          ) : direction === 'inbound' && Selfie ? (
            <img
              src={Selfie}
              alt={L.applicant.applicant}
              className="w-full h-full object-cover"
            />
          ) : author.startsWith('+') ? (
            <Icon name="UserIcon" className="h-5 w-5 text-white" />
          ) : (
            <span className="text-white font-medium text-sm">
              {author.charAt(0).toUpperCase()}
            </span>
          )}
        </div>

        {/* Message Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-semibold text-gray-900">{author === 'System' ? L.applicant.comms.system : author}</span>
            {(message.kind === 'sms' || message.kind === 'mms') && (
              <span className="inline-flex items-center px-2 py-0.5 border border-gray-300 rounded text-xs text-gray-600">{L.applicant.comms.via_sms}</span>
            )}
            {message.kind === 'email' && (
              <span className="inline-flex items-center px-2 py-0.5 border border-gray-300 rounded text-xs text-gray-600">{L.applicant.comms.via_email}</span>
            )}
            {getStatusBadge()}
            <span className="text-xs text-gray-500">
              {timestampPrefix}
              <DateDisplay
                utcValue={displayTime}
                variant="datetime"
                displayInUTC={false}
                options={{
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                }}
                className="!text-xs !text-gray-500"
              />
            </span>
          </div>

          {/* SMS/MMS subtitle and details */}
          {(message.kind === 'sms' || message.kind === 'mms') && (
            <div className="mb-2">
              <div className="text-xs text-gray-500">
                {direction === 'inbound' ? L.applicant.comms.inbound_sms : L.applicant.comms.outbound_sms}
              </div>
              <div className="text-xs text-gray-500">
                {L.applicant.comms.from.replace('$from', message.source)} → {L.applicant.comms.to.replace('$to', message.destination)}
              </div>
            </div>
          )}

          {/* Email subtitle and details */}
          {message.kind === 'email' && (
            <div className="mb-2">
              <div className="text-xs text-gray-500 mb-1">
                {direction === 'inbound' ? L.applicant.comms.inbound_email : L.applicant.comms.outbound_email}
              </div>
              <div className="text-xs text-gray-500">
                {L.applicant.comms.from.replace('$from', message.source)} → {L.applicant.comms.to.replace('$to', message.destination)}
              </div>
            </div>
          )}

          {/* Message Kind-Specific Rendering */}
          {message.kind === 'call' && (() => {
            const recordingUrl = metadata.recording_url || metadata.RecordingUrl;

            return (
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-2">
                  {direction === 'outbound' ? L.applicant.comms.outbound_call_short : L.applicant.comms.inbound_call_short}
                </div>
                <div className="text-xs text-gray-500 mb-2">
                  {L.applicant.comms.from.replace('$from', message.source)} → {L.applicant.comms.to.replace('$to', message.destination)}
                </div>
                {recordingUrl ? (
                  <div className="bg-gray-50/50 border rounded-xl p-1">
                    <AudioPlayer
                      item={{
                        id: String(message.id),
                        src: recordingUrl,
                      }}
                      className="max-w-none"
                    />
                  </div>
                ) : metadata.isInboundVoiceRecordingDisabled ? (
                  <div className="text-sm text-gray-500 italic">{L.applicant.comms.inbound_voice_recordings_disabled}</div>
                ) : (
                  <div className="text-sm text-gray-500 italic">{L.applicant.comms.no_recording_available}</div>
                )}
                {message.transcript && (
                  <div className="mt-3 text-sm text-gray-600 italic border-t pt-2">
                    <div className="font-medium text-gray-700 mb-1">{L.applicant.comms.transcription}:</div>
                    {message.transcript}
                  </div>
                )}
              </div>
            );
          })()}

          {(message.kind === 'sms' || message.kind === 'mms' || message.kind === 'whatsapp') && (() => {
            // Check for audio in various metadata fields
            let audioPath = null;
            let needsSignedUrl = false;

            // First check for direct URLs (RecordingUrl, MediaUrl0)
            if (metadata.RecordingUrl && metadata.RecordingUrl.trim()) {
              audioPath = metadata.RecordingUrl;
            } else if (metadata.MediaUrl0 && metadata.MediaUrl0.includes('audio')) {
              audioPath = metadata.MediaUrl0;
            }
            // Check if there's an audio attachment in metadata.attachments array
            else if (metadata.attachments && Array.isArray(metadata.attachments)) {
              const attachmentTypes = metadata.attachmentTypes || [];
              for (let i = 0; i < metadata.attachments.length; i++) {
                const attachmentType = attachmentTypes[i] || '';
                if (attachmentType.includes('audio')) {
                  audioPath = metadata.attachments[i];
                  needsSignedUrl = true; // Attachments need to be converted to signed URLs
                  break;
                }
              }
            }

            const hasAudio = !!audioPath;

            // Detect blank WhatsApp messages (only attachments, no text)
            const blankWhatsAppMsg = message.kind === 'whatsapp' &&
              message.message.trim().length === 0 &&
              !(metadata.attachments?.length);

            return (
              <>
                {hasAudio && audioPath ? (
                  needsSignedUrl ? (
                    <AudioPlayerWithSignedUrl audioPath={audioPath} messageId={String(message.id)} direction={direction} />
                  ) : (
                    <div className="bg-gray-50/50 border rounded-xl p-1">
                      <AudioPlayer
                        item={{
                          id: String(message.id),
                          src: audioPath,
                        }}
                      />
                    </div>
                  )
                ) : message.message ? (
                  <div className="bg-gray-50/50 border text-sm rounded-xl p-2.5">
                    {message.renderedMessage ? (
                      <div dangerouslySetInnerHTML={{ __html: message.renderedMessage }} />
                    ) : (
                      prettyTextMessage(message.message || '', blankWhatsAppMsg)
                    )}
                  </div>
                ) : null}
              </>
            );
          })()}

          {message.kind === 'email' && (
            <div className="bg-gray-50/50 border rounded-xl p-2.5">
              <div className="text-sm">
                {message.renderedMessage ? (
                  <div className="markdown overflow-x-auto whitespace-normal" dangerouslySetInnerHTML={{ __html: message.renderedMessage }} />
                ) : (
                  prettyTextMessage(message.message || '')
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
